<script setup lang="ts">
import { mdiMap, mdiDiamondStone, mdiGiftOutline, mdiGithub, mdiLink, mdiLinkOff, mdiPlus, mdiOwl, mdiCheckboxBlankOutline, mdiCheckboxOutline, mdiBookshelf } from '@mdi/js'
import type { GrimoireItem } from '~/types/interfaces'

/**
 * 魔導の書庫（vault）用: カテゴリ絞り込みチャコール + カードグリッド。
 *
 * 選択/open の調停とカード描画は `GrimoireCardGrid`（コンテナ）に委譲する。
 * 本コンポーネントはチャコール（カテゴリ v-checkbox 群・GitHub 行・追加ボタン）と
 * カテゴリでの絞り込み/並べ替えだけを担う。
 */
const props = defineProps<{
  items: GrimoireItem[]
  hideAddButton?: boolean
  crystallizeTypes?: string[]
  categories?: string[]
  githubAvailable?: boolean
  githubConnected?: boolean
  githubUsername?: string | null
}>()
const emit = defineEmits([
  'add',
  'delete',
  'select',
  'detail',
  'crystallize',
  'rename',
  'github-connect',
  'github-disconnect',
  // 各カードの主アクション (排他)
  'program',
  'read',
  'play',
])

const categoriesToShow = computed<string[]>(() => props.categories ?? ['user', 'encodia'])
const grimoireFilters = ref<string[]>([...categoriesToShow.value])

// When new categories are added (e.g. github after connection check), auto-check them
watch(categoriesToShow, (newCats) => {
  for (const cat of newCats) {
    if (!grimoireFilters.value.includes(cat)) {
      grimoireFilters.value.push(cat)
    }
  }
  // Remove categories that are no longer shown (e.g. github after disconnect)
  grimoireFilters.value = grimoireFilters.value.filter(c => newCats.includes(c))
})

// filter grimoires by categories and sorted by category order
const filteredGrimoires = computed<GrimoireItem[]>(() =>
  props.items.filter(item =>
    grimoireFilters.value.includes(item.type),
  ).sort((a, b) => {
    const aIndex = categoriesToShow.value.indexOf(a.type)
    const bIndex = categoriesToShow.value.indexOf(b.type)

    if (aIndex !== bIndex) {
      return aIndex - bIndex
    }

    if (a.createdAt < b.createdAt) {
      return 1
    }
    else if (a.createdAt > b.createdAt) {
      return -1
    }
    else {
      return 0
    }
  },
  ),
)
// 絞り込みで開いていたカードが消えた場合の選択解除は GrimoireCardGrid が
// items 変化を監視して行う（select(null) が上がってくる）。

const onClickAdd = () => {
  emit('add')
}
</script>

<template>
  <div class="grimoire-selector">
    <!-- Category filters -->
    <div class="grimoire-selector-area">
      <v-checkbox
        v-if="categoriesToShow.includes('user')"
        v-model="grimoireFilters"
        multiple
        value="user"
        hide-details
        :false-icon="mdiCheckboxBlankOutline"
        :true-icon="mdiCheckboxOutline"
      >
        <template #label>
          <span class="grimoire-selector-label"><v-icon :icon="mdiBookshelf" />あなたの魔導書</span>
        </template>
      </v-checkbox>
      <v-btn
        v-if="!hideAddButton"
        density="compact"
        color="purple"
        icon
        data-test-id="add-grimoire-button"
        @click="onClickAdd()"
      >
        <v-icon :icon="mdiPlus" />
      </v-btn>
      <v-checkbox
        v-if="categoriesToShow.includes('encodia')"
        v-model="grimoireFilters"
        multiple
        value="encodia"
        hide-details
        :false-icon="mdiCheckboxBlankOutline"
        :true-icon="mdiCheckboxOutline"
      >
        <template #label>
          <span class="grimoire-selector-label"><v-icon :icon="mdiMap" />エンコーディア</span>
        </template>
      </v-checkbox>
      <v-checkbox
        v-if="categoriesToShow.includes('crystal')"
        v-model="grimoireFilters"
        multiple
        value="crystal"
        hide-details
        :false-icon="mdiCheckboxBlankOutline"
        :true-icon="mdiCheckboxOutline"
      >
        <template #label>
          <span class="grimoire-selector-label"><v-icon :icon="mdiDiamondStone" />デコード済</span>
        </template>
      </v-checkbox>
      <v-checkbox
        v-if="categoriesToShow.includes('privilege')"
        v-model="grimoireFilters"
        multiple
        value="privilege"
        hide-details
        :false-icon="mdiCheckboxBlankOutline"
        :true-icon="mdiCheckboxOutline"
      >
        <template #label>
          <span class="grimoire-selector-label"><v-icon :icon="mdiGiftOutline" />研究会特典</span>
        </template>
      </v-checkbox>
      <v-checkbox
        v-if="categoriesToShow.includes('encodia_reference')"
        v-model="grimoireFilters"
        multiple
        value="encodia_reference"
        hide-details
        :false-icon="mdiCheckboxBlankOutline"
        :true-icon="mdiCheckboxOutline"
      >
        <template #label>
          <span class="grimoire-selector-label"><v-icon :icon="mdiMap" />エンコーディア参考</span>
        </template>
      </v-checkbox>
      <!-- GitHub: connected → enabled checkbox with username + disconnect button -->
      <template v-if="githubAvailable && githubConnected">
        <v-checkbox
          v-model="grimoireFilters"
          multiple
          value="github"
          hide-details
          :false-icon="mdiCheckboxBlankOutline"
          :true-icon="mdiCheckboxOutline"
        >
          <template #label>
            <span class="grimoire-selector-label"><v-icon :icon="mdiGithub" />{{ githubUsername }}</span>
          </template>
        </v-checkbox>
        <v-btn
          density="compact"
          variant="text"
          size="x-small"
          :icon="mdiLinkOff"
          title="GitHub 連携を解除"
          @click="emit('github-disconnect')"
        />
      </template>
      <!-- GitHub: not connected → disabled checkbox "八本足の猫" + connect button -->
      <template v-else-if="githubAvailable">
        <v-checkbox
          hide-details
          disabled
          :false-icon="mdiCheckboxBlankOutline"
        >
          <template #label>
            <span class="grimoire-selector-label"><v-icon :icon="mdiGithub" />八本足の猫</span>
          </template>
        </v-checkbox>
        <v-btn
          density="compact"
          variant="text"
          size="x-small"
          :icon="mdiLink"
          title="GitHub と接続する"
          @click="emit('github-connect')"
        />
      </template>
      <v-checkbox
        v-if="categoriesToShow.includes('snapshot')"
        v-model="grimoireFilters"
        multiple
        value="snapshot"
        hide-details
        :false-icon="mdiCheckboxBlankOutline"
        :true-icon="mdiCheckboxOutline"
      >
        <template #label>
          <span class="grimoire-selector-label"><v-icon :icon="mdiOwl" />ふくろう便</span>
        </template>
      </v-checkbox>
    </div>

    <!-- Grimoire list: 選択/open 調停とカード描画は GrimoireCardGrid が持つ -->
    <CradleGrimoireCardGrid
      :items="filteredGrimoires"
      :crystallize-types="crystallizeTypes"
      @select="emit('select', $event)"
      @program="emit('program', $event)"
      @read="emit('read', $event)"
      @play="emit('play', $event)"
      @detail="emit('detail', $event)"
      @crystallize="emit('crystallize', $event)"
      @rename="emit('rename', $event)"
      @delete="emit('delete', $event)"
    />
  </div>
</template>

<style scoped>
.grimoire-selector {
  height: 100%;

  display: flex;
  flex-direction: column;
}

.grimoire-selector-area {
  flex: 0 0 auto;
  display: flex;
  gap: max(8px, calc(1 * var(--u)));
  align-items: center;
  background-color: rgb(224, 224, 224, 0.7);
  height: max(28px, calc(3.75 * var(--u)));
  padding: 0 max(4px, calc(0.5 * var(--u)));
}

.grimoire-selector-area :deep(.v-input) {
  flex: 0 0 auto;
  --v-input-control-height: max(28px, calc(3.75 * var(--u)));
}

.grimoire-selector-area :deep(.v-selection-control) {
  min-height: auto;
  height: max(28px, calc(3.75 * var(--u)));
}

.grimoire-selector-area :deep(.v-selection-control__wrapper) {
  height: max(16px, calc(2 * var(--u)));
  width: max(16px, calc(2 * var(--u)));
}

.grimoire-selector-area :deep(.v-icon) {
  font-size: max(14px, calc(1.875 * var(--u)));
}

.grimoire-selector-area :deep(.v-btn) {
  height: max(20px, calc(2.5 * var(--u)));
  width: max(20px, calc(2.5 * var(--u)));
}

.grimoire-selector-label {
  font-size: max(8.89px, calc(1.25 * var(--u)));
}
</style>
